import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'

const ENV_META = {
  staging: {
    label: 'Staging',
    description: 'For integration testing',
  },
  production: {
    label: 'Production',
    description: 'For live traffic',
    danger: true,
  },
}

function formatCreatedAt(iso) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

/**
 * Generate/rotate a campaign's staging + production API keys.
 *
 * Either pass a fixed `campaignId` (+ `campaignName`) when the caller
 * already knows which campaign it's managing, or omit them and pass
 * `affiliateId` alone to show a campaign picker scoped to that affiliate.
 */
export default function ApiKeysModal({ affiliateId, affiliateName, campaignId: fixedCampaignId, campaignName: fixedCampaignName, onClose }) {
  const [campaigns, setCampaigns] = useState([])
  const [campaignsLoading, setCampaignsLoading] = useState(!fixedCampaignId)
  const [campaignId, setCampaignId] = useState(fixedCampaignId ? String(fixedCampaignId) : '')
  const [loadingEnv, setLoadingEnv] = useState(null)
  const [keys, setKeys] = useState({})
  const [status, setStatus] = useState({})
  const [statusLoading, setStatusLoading] = useState(false)
  const [confirmEnv, setConfirmEnv] = useState(null)
  const [confirmText, setConfirmText] = useState('')

  useEffect(() => {
    if (fixedCampaignId) return
    let active = true
    setCampaignsLoading(true)
    api.get('/campaigns', { params: { affiliate_profile_id: affiliateId, limit: 100 } })
      .then(({ data: res }) => {
        if (!active) return
        const items = Array.isArray(res) ? res : (res.items || res.data || [])
        setCampaigns(items)
        if (items.length > 0) setCampaignId(String(items[0].id))
      })
      .catch(() => { if (active) toast.error('Error loading campaigns') })
      .finally(() => { if (active) setCampaignsLoading(false) })
    return () => { active = false }
  }, [affiliateId, fixedCampaignId])

  useEffect(() => {
    if (!campaignId) return
    let active = true
    setStatusLoading(true)
    api.get(`/affiliates/${affiliateId}/campaigns/${campaignId}/credentials`)
      .then(({ data }) => {
        if (!active) return
        const byEnv = {}
        for (const row of data) byEnv[row.environment] = row
        setStatus(prev => ({ ...prev, [campaignId]: byEnv }))
      })
      .catch(() => { if (active) toast.error('Error loading key status') })
      .finally(() => { if (active) setStatusLoading(false) })
    return () => { active = false }
  }, [affiliateId, campaignId])

  const rotateKey = async (env) => {
    setLoadingEnv(env)
    try {
      const { data } = await api.post(`/affiliates/${affiliateId}/campaigns/${campaignId}/credentials/${env}/rotate`)
      setKeys(prev => ({ ...prev, [campaignId]: { ...prev[campaignId], [env]: data.api_key } }))
      setStatus(prev => ({
        ...prev,
        [campaignId]: {
          ...prev[campaignId],
          [env]: { environment: env, has_active_key: true, credential_id: data.id, created_at: new Date().toISOString() },
        },
      }))
      toast.success(`${env} key rotated successfully!`)
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || `Failed to rotate ${env} key`)
    } finally {
      setLoadingEnv(null)
    }
  }

  const requestRotate = (env) => {
    if (ENV_META[env].danger) {
      setConfirmText('')
      setConfirmEnv(env)
      return
    }
    if (!window.confirm(`Are you sure you want to rotate the ${env} key for this campaign? The old key will immediately stop working.`)) return
    rotateKey(env)
  }

  const confirmDangerousRotate = () => {
    if (confirmText.trim().toLowerCase() !== 'production') return
    const env = confirmEnv
    setConfirmEnv(null)
    setConfirmText('')
    rotateKey(env)
  }

  const campaignKeys = keys[campaignId] || {}
  const campaignStatus = status[campaignId] || {}
  const selectedCampaign = fixedCampaignId
    ? { id: fixedCampaignId, name: fixedCampaignName }
    : campaigns.find(c => String(c.id) === String(campaignId))

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h2>API Keys{affiliateName ? ` · ${affiliateName}` : ''}{!affiliateName && selectedCampaign?.name ? ` · ${selectedCampaign.name}` : ''}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
            Generate or rotate API keys for a campaign. Keys are scoped to one campaign
            — a key issued here will not authenticate submissions for any other campaign,
            even one owned by this same affiliate. Keys are shown EXACTLY ONCE. If lost,
            you must rotate them again. Rotating a key instantly invalidates the old one.
          </p>

          {campaignsLoading ? (
            <p style={{ color: 'var(--text-muted)' }}>Loading campaigns…</p>
          ) : !fixedCampaignId && campaigns.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>
              This affiliate has no campaigns yet — create a campaign first, then come back here to issue its API key.
            </p>
          ) : (
            <>
              {!fixedCampaignId && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Campaign</label>
                  <select
                    className="select"
                    value={campaignId}
                    onChange={e => setCampaignId(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {['staging', 'production'].map(env => {
                const meta = ENV_META[env]
                const envStatus = campaignStatus[env]
                return (
                  <div
                    key={env}
                    className="card"
                    style={{
                      padding: 16,
                      marginBottom: 16,
                      border: meta.danger ? '1px solid var(--red)' : undefined,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12 }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                          {meta.label} Environment
                          {meta.danger && <span className="badge badge-red" style={{ fontSize: 10 }}>LIVE TRAFFIC</span>}
                        </h3>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{meta.description}</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                          {statusLoading
                            ? 'Checking current status…'
                            : envStatus?.has_active_key
                              ? `Active key issued${formatCreatedAt(envStatus.created_at) ? ` · ${formatCreatedAt(envStatus.created_at)}` : ''}`
                              : 'No active key'}
                        </p>
                      </div>
                      <button
                        className={meta.danger ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
                        onClick={() => requestRotate(env)}
                        disabled={loadingEnv === env}
                      >
                        {envStatus?.has_active_key ? 'Rotate' : 'Generate'} {meta.label} Key
                      </button>
                    </div>

                    {campaignKeys[env] && (
                      <div style={{ background: 'var(--bg-surface)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: 11, fontWeight: 'bold', color: 'var(--amber)', marginBottom: 4 }}>
                          ATTENTION: Copy this key now! It will never be shown again.
                        </div>
                        <code style={{ fontSize: 14, wordBreak: 'break-all' }}>{campaignKeys[env]}</code>
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>

      {confirmEnv && (
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); setConfirmEnv(null) }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2>Confirm production key rotation</h2>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>
                This will immediately invalidate the current <strong>production</strong> key for
                {' '}{selectedCampaign?.name || 'this campaign'}. Any live traffic still using the old key
                will start failing right away.
              </p>
              <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
                Type <strong>production</strong> below to confirm.
              </p>
              <input
                className="input"
                autoFocus
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="production"
                style={{ marginTop: 8 }}
                onKeyDown={e => { if (e.key === 'Enter') confirmDangerousRotate() }}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setConfirmEnv(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={confirmText.trim().toLowerCase() !== 'production'}
                onClick={confirmDangerousRotate}
              >
                Rotate production key
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
